/**
 * Круглый флаг страны.
 *
 * Нарисован в SVG, а не эмодзи: флаги-эмодзи в Telegram Desktop на Windows
 * и на части Android показываются двумя буквами («DE»), и ряд серверов
 * превращается в набор кодов.
 */
import type { ReactNode } from 'react';

export type CountryCode = 'de' | 'nl' | 'fi' | 'ee' | 'se' | 'ru' | 'pl' | 'fr' | 'us' | 'tr' | 'kz';

export const COUNTRY_NAMES: Record<CountryCode, string> = {
  de: 'Германия',
  nl: 'Нидерланды',
  fi: 'Финляндия',
  ee: 'Эстония',
  se: 'Швеция',
  ru: 'Россия',
  pl: 'Польша',
  fr: 'Франция',
  us: 'США',
  tr: 'Турция',
  kz: 'Казахстан',
};

/** Полосы: горизонтальные (h) или вертикальные (v), равной ширины. */
function stripes(dir: 'h' | 'v', colors: string[]): ReactNode {
  const size = 24 / colors.length;
  return colors.map((color, i) =>
    dir === 'h' ? (
      <rect key={color + i} x="0" y={i * size} width="24" height={size} fill={color} />
    ) : (
      <rect key={color + i} x={i * size} y="0" width={size} height="24" fill={color} />
    ),
  );
}

/** Скандинавский крест. */
function nordic(field: string, cross: string): ReactNode {
  return (
    <>
      <rect width="24" height="24" fill={field} />
      <rect x="7" y="0" width="4" height="24" fill={cross} />
      <rect x="0" y="10" width="24" height="4" fill={cross} />
    </>
  );
}

const ART: Record<CountryCode, ReactNode> = {
  de: stripes('h', ['#1a1a1a', '#dd0000', '#ffce00']),
  nl: stripes('h', ['#ae1c28', '#ffffff', '#21468b']),
  pl: stripes('h', ['#ffffff', '#dc143c']),
  ee: stripes('h', ['#0072ce', '#1a1a1a', '#ffffff']),
  ru: stripes('h', ['#ffffff', '#0039a6', '#d52b1e']),
  fr: stripes('v', ['#0055a4', '#ffffff', '#ef4135']),
  fi: nordic('#ffffff', '#003580'),
  se: nordic('#006aa7', '#fecc00'),
  us: (
    <>
      <rect width="24" height="24" fill="#ffffff" />
      {/* 13 полос: 7 красных, 6 белых. */}
      {Array.from({ length: 7 }, (_, i) => (
        <rect key={i} x="0" y={(i * 2 * 24) / 13} width="24" height={24 / 13} fill="#b22234" />
      ))}
      {/* Синий крыж на 7 полос с рядами звёзд в шахматном порядке. */}
      <rect x="0" y="0" width="12" height={(7 * 24) / 13} fill="#3c3b6e" />
      {Array.from({ length: 5 }, (_, row) =>
        Array.from({ length: row % 2 ? 3 : 4 }, (_, col) => (
          <circle
            key={`${row}-${col}`}
            cx={(row % 2 ? 3.4 : 1.9) + col * 3}
            cy={1.9 + row * 2.3}
            r="0.6"
            fill="#ffffff"
          />
        )),
      )}
    </>
  ),
  tr: (
    <>
      <rect width="24" height="24" fill="#e30a17" />
      <circle cx="10" cy="12" r="5" fill="#ffffff" />
      <circle cx="11.3" cy="12" r="4" fill="#e30a17" />
      <circle cx="16" cy="12" r="1.6" fill="#ffffff" />
    </>
  ),
  kz: (
    <>
      <rect width="24" height="24" fill="#00afca" />
      <circle cx="12" cy="11" r="4" fill="#fec50c" />
    </>
  ),
};

export function Flag({ code, size = 32 }: { code: CountryCode; size?: number }) {
  const clip = `flag-${code}`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label={COUNTRY_NAMES[code]}>
      <defs>
        <clipPath id={clip}>
          <circle cx="12" cy="12" r="12" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>{ART[code]}</g>
      {/* Тонкий обод: светлые флаги (Финляндия, Польша) иначе
          сливаются с белыми элементами рядом. */}
      <circle cx="12" cy="12" r="11.5" fill="none" stroke="rgb(255 255 255 / 18%)" />
    </svg>
  );
}
